import { useCallback, useEffect, useRef } from 'react';

export default function useVideoKeepAlive(videoRef, { enabled = true, onPlayRejected } = {}) {
  const visible = useRef(false);
  const enabledRef = useRef(enabled);
  const rejectedRef = useRef(onPlayRejected);
  enabledRef.current = enabled;
  rejectedRef.current = onPlayRejected;

  const attemptPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !enabledRef.current || !visible.current || document.hidden) return;
    try {
      const result = video.play();
      result?.catch?.((error) => {
        if (error && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return;
        rejectedRef.current?.(error);
      });
    } catch (error) {
      rejectedRef.current?.(error);
    }
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      visible.current = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      if (visible.current && enabledRef.current) attemptPlay();
      else video.pause();
    }, { threshold: [0, 0.25] });

    const onVisibilityChange = () => {
      if (document.hidden) video.pause();
      else attemptPlay();
    };
    const onFirstTouch = () => attemptPlay();
    const onCanPlay = () => attemptPlay();

    observer.observe(video);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('touchstart', onFirstTouch, { once: true, passive: true });
    video.addEventListener('canplay', onCanPlay);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('touchstart', onFirstTouch);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [attemptPlay, enabled, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    if (enabled) attemptPlay();
    else video.pause();
  }, [attemptPlay, enabled, videoRef]);
}
